<template>
  <div class="private-fund-details" :class="[bgColor, { 'is-pc': !isApp }]">
    <FundNavBar v-if="!isApp" />
    <div class="private-fund-details__scroll">
      <div class="private-fund-details__summary">
        <p class="private-fund-details__name">{{ detail.product_name }}</p>
        <div class="private-fund-details__metrics">
          <div class="private-fund-details__metric is-aum">
            <p class="private-fund-details__metric-value">{{ detail.assets_under_management }}</p>
            <p class="private-fund-details__metric-label is-aum-label">{{ assetsUnderManagementLabel }}</p>
          </div>
          <div class="private-fund-details__metric is-return">
            <p class="private-fund-details__metric-value" :class="getReturnTrendClass(detail.ytd_return)">{{ detail.ytd_return }}</p>
            <p class="private-fund-details__metric-label is-return-nowrap">{{ $t("privateFund.list.returnYtd") }}</p>
          </div>
          <div class="private-fund-details__metric is-return">
            <p class="private-fund-details__metric-value" :class="getReturnTrendClass(detail.since_inception_return)">{{ detail.since_inception_return }}</p>
            <p
              class="private-fund-details__metric-label"
              :class="isEn ? 'is-return-label' : 'is-return-nowrap'"
            >{{ $t("privateFund.list.returnSinceInception") }}</p>
          </div>
        </div>
      </div>

      <div class="private-fund-details__tabs">
        <button
          type="button"
          class="private-fund-details__tab"
          :class="{ 'is-active': activeTab === 'overview' }"
          @click="activeTab = 'overview'"
        >
          {{ $t("privateFund.tabOverview") }}
        </button>
        <button
          type="button"
          class="private-fund-details__tab"
          :class="{ 'is-active': activeTab === 'performance' }"
          @click="activeTab = 'performance'"
        >
          {{ $t("privateFund.tabPerformance") }}
        </button>
      </div>

      <template v-if="activeTab === 'overview'">
        <div class="private-fund-details__section">
          <p class="private-fund-details__section-title">{{ $t("privateFund.sectionOverview") }}</p>
          <div
            v-for="item in overviewItems"
            :key="item.key"
            class="private-fund-details__row"
          >
            <span class="private-fund-details__label">{{ item.label }}</span>
            <span class="private-fund-details__value">{{ item.value }}</span>
          </div>
        </div>

      <div class="private-fund-details__section">
        <p class="private-fund-details__section-title">{{ $t("privateFund.sectionSubscription") }}</p>
        <div
          v-for="item in subscriptionItems"
          :key="item.key"
          class="private-fund-details__row"
        >
          <span class="private-fund-details__label">{{ item.label }}</span>
          <span class="private-fund-details__value">{{ item.value }}</span>
        </div>
      </div>

      <div class="private-fund-details__section">
        <p class="private-fund-details__section-title">{{ $t("privateFund.sectionRedemption") }}</p>
        <div
          v-for="item in redemptionItems"
          :key="item.key"
          class="private-fund-details__row"
        >
          <span class="private-fund-details__label">{{ item.label }}</span>
          <span class="private-fund-details__value">{{ item.value }}</span>
        </div>
      </div>

      <div class="private-fund-details__section">
        <p class="private-fund-details__section-title">{{ $t("privateFund.sectionFees") }}</p>
        <div
          v-for="item in feeItems"
          :key="item.key"
          class="private-fund-details__row"
        >
          <span class="private-fund-details__label">{{ item.label }}</span>
          <span class="private-fund-details__value">{{ item.value }}</span>
        </div>
      </div>

      <div
        v-if="detail.investment_strategy"
        class="private-fund-details__section private-fund-details__section--text"
      >
        <p class="private-fund-details__section-title">{{ $t("privateFund.sectionStrategy") }}</p>
        <p class="private-fund-details__paragraph">{{ $t("privateFund.strategyPrefix") }}{{ detail.investment_strategy }}</p>
      </div>

      <DisclaimerFooterLink />
      </template>

      <div v-else class="private-fund-details__performance">
        <div class="private-fund-details__performance-card">
          <FundNavChartChrome :has-data="hasPerformanceChartData">
            <FundNavChart :nav-list="navList" :chart-key="id || 'detail'" />
          </FundNavChartChrome>
        </div>

        <DisclaimerFooterLink />
      </div>
    </div>

    <div class="private-fund-details__footer">
      <button type="button" class="private-fund-details__btn is-primary" @click="handleLearnMore">
        {{ $t("privateFund.learnMore") }}
      </button>
      <button type="button" class="private-fund-details__btn is-dark" @click="handleSubscribe">
        {{ $t("privateFund.subscribeApply") }}
      </button>
    </div>

    <GlobalActionSheet v-model="showServiceSheet" />
  </div>
</template>

<script>
import GlobalActionSheet from "@/components/GlobalActionSheet.vue";
import { getBrowser } from "@/utils/browser";
import { getAppToken, webViewHeaderRightIcons } from "@/utils/hybirdMethods";
import {
  getReturnTrendClass,
  mapFundDetail,
  getAgreementStorageKey,
  resolveAssetsUnderManagementLabel
} from "../utils";
import FundNavBar from "../components/FundNavBar.vue";
import FundNavChart from "../components/FundNavChart.vue";
import FundNavChartChrome from "../components/FundNavChartChrome.vue";
import DisclaimerFooterLink from "../components/DisclaimerFooterLink.vue";

const browser = getBrowser();

export default {
  components: {
    GlobalActionSheet,
    FundNavBar,
    FundNavChart,
    FundNavChartChrome,
    DisclaimerFooterLink
  },
  data() {
    return {
      isApp: browser.isApp,
      activeTab: "overview",
      id: "",
      showServiceSheet: false,
      agreementUrls: {},
      detail: {
        id: "",
        product_name: "",
        assets_under_management: "",
        assets_under_management_unit: 1,
        assets_under_management_unit_display: "",
        ytd_return: "",
        since_inception_return: "",
        fund_company: "",
        currency_display: "",
        subscription_day: "",
        subscription_price_display: "",
        min_subscription_amount: "",
        additional_subscription_amount: "",
        share_lock_up: "",
        redemption_day: "",
        min_redemption: "",
        min_holding: "",
        subscription_fee: "",
        redemption_fee: "",
        management_fee: "",
        investment_strategy: ""
      },
      navList: [],
    };
  },
  computed: {
    bgColor() {
      return this.$store.getters["common/bgColor"];
    },
    isEn() {
      return this.$i18n.locale === "en";
    },
    assetsUnderManagementLabel() {
      return resolveAssetsUnderManagementLabel(
        this.$t.bind(this),
        this.detail.assets_under_management_unit,
        this.detail.assets_under_management_unit_display
      );
    },
    overviewItems() {
      const d = this.detail;
      return [
        { key: "product_name", label: this.$t("privateFund.productName"), value: d.product_name },
        { key: "fund_company", label: this.$t("privateFund.fundCompany"), value: d.fund_company },
        { key: "currency_display", label: this.$t("privateFund.currency"), value: d.currency_display }
      ];
    },
    subscriptionItems() {
      const d = this.detail;
      return [
        { key: "subscription_day", label: this.$t("privateFund.subscriptionDay"), value: d.subscription_day },
        { key: "min_subscription_amount", label: this.$t("privateFund.minSubscription"), value: d.min_subscription_amount },
        { key: "additional_subscription_amount", label: this.$t("privateFund.additionalSubscription"), value: d.additional_subscription_amount }
      ];
    },
    redemptionItems() {
      const d = this.detail;
      return [
        { key: "share_lock_up", label: this.$t("privateFund.shareLockUp"), value: d.share_lock_up },
        { key: "redemption_day", label: this.$t("privateFund.redemptionDay"), value: d.redemption_day },
        { key: "min_redemption", label: this.$t("privateFund.minRedemption"), value: d.min_redemption },
        { key: "min_holding", label: this.$t("privateFund.minHolding"), value: d.min_holding }
      ];
    },
    feeItems() {
      const d = this.detail;
      return [
        { key: "subscription_fee", label: this.$t("privateFund.subscriptionFee"), value: d.subscription_fee },
        { key: "redemption_fee", label: this.$t("privateFund.redemptionFee"), value: d.redemption_fee },
        { key: "management_fee", label: this.$t("privateFund.managementFee"), value: d.management_fee }
      ];
    },
    hasPerformanceChartData() {
      return this.navList.length > 0;
    }
  },
  async created() {
    this.id = this.$route.query.id;
    this.updateWebViewHeaderRightIcons(true, true);
    await this.initAppToken();
    this.loadDetail();
  },
  beforeDestroy() {
    //this.updateWebViewHeaderRightIcons(true, true);
  },
  methods: {
    getReturnTrendClass,
    updateWebViewHeaderRightIcons(showSearch, showMessage) {
      webViewHeaderRightIcons({ showSearch, showMessage });
    },
    async initAppToken() {
      if (!browser.isApp) {
        return;
      }
      try {
        const res = await getAppToken();
        if (res === "isGetPro" || !res) {
          return;
        }
        const userInfo = JSON.parse(res);
        if (userInfo?.token) {
          localStorage.setItem("token", userInfo.token);
          sessionStorage.setItem("token", userInfo.token);
        }
      } catch (error) {
        console.log("[private-fund/details] 获取 App token 失败:", error);
      }
    },
    async loadDetail() {
      if (!this.id) {
        return;
      }
      try {
        const res = await this.$apis.getFundProductDetail({ id: this.id });
        const mapped = mapFundDetail(res);
        this.detail = mapped.detail;
        this.agreementUrls = mapped.agreementUrls;
        this.loadNavHistory();
      } catch (error) {
        console.log("[private-fund/details] 获取详情失败:", error);
      }
    },
    async loadNavHistory() {
      if (!this.id) {
        return;
      }
      try {
        const res = await this.$apis.getFundProductNavHistory({
          product_id: this.id
        });
        this.navList = res?.nav_list || [];
      } catch (error) {
        console.log("[private-fund/details] 获取净值走势失败:", error);
        this.navList = [];
      }
    },
    handleLearnMore() {
      this.showServiceSheet = true;
    },
    handleSubscribe() {
      if (this.id && Object.keys(this.agreementUrls).length) {
        sessionStorage.setItem(
          getAgreementStorageKey(this.id),
          JSON.stringify(this.agreementUrls)
        );
      }
      this.$router.push({
        path: "/private-fund/fundSubscribe",
        query: {
          ...this.$route.query,
          id: this.id,
          name: this.detail.product_name,
          netValue: this.detail.assets_under_management,
          currency: this.detail.currency_display,
          minSubscription: this.detail.min_subscription_amount,
          additionalSubscription: this.detail.additional_subscription_amount
        }
      });
    }
  }
};
</script>

<style scoped lang="less">
.private-fund-details {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #fff;

  &.is-pc {
    padding-top: 100px;
    box-sizing: border-box;
  }

  &__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-top: 24px;
    padding-bottom: 8px;
  }

  &__summary {
    margin: 0 24px 0;
    padding: 32px 28px;
    background: #fff;
    border-radius: 24px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  }

  &__name {
    color: #171717;
    font-size: 34px;
    font-weight: 600;
    line-height: 48px;
    word-break: break-word;
  }

  &__metrics {
    display: flex;
    align-items: flex-start;
    gap: 48px;
    margin-top: 28px;
  }

  &__metric {
    min-width: 0;

    &.is-aum {
      flex: 1 1 0;
      min-width: 0;
    }

    &.is-return {
      flex: 0 0 auto;
    }
  }

  &__metric-value {
    color: #171717;
    font-size: 40px;
    font-weight: 600;
    line-height: 56px;
    min-height: 56px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.is-rise {
      color: #e83324;
    }

    &.is-fall {
      color: #02aa39;
    }

    &.is-flat {
      color: #999;
    }
  }

  &__metric-label {
    margin-top: 4px;
    color: #999;
    font-size: 22px;
    line-height: 32px;

    &.is-aum-label {
      word-break: break-word;
    }

    &.is-return-label {
      white-space: pre-line;
    }

    &.is-return-nowrap {
      white-space: nowrap;
    }
  }

  &__tabs {
    display: flex;
    gap: 48px;
    margin-top: 32px;
    padding: 0 24px;
    border-bottom: 1px solid #ebebeb;
  }

  &__tab {
    position: relative;
    padding: 20px 0 24px;
    border: none;
    background: transparent;
    color: #9ca3af;
    font-size: 30px;
    line-height: 42px;
    cursor: pointer;

    &.is-active {
      color: #1353c8;
      font-weight: 600;

      &::after {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 4px;
        background: #1353c8;
        border-radius: 2px;
      }
    }
  }

  &__performance {
    padding: 32px 24px 0;
  }

  &__performance-card {
    padding: 32px 28px;
    background: #fff;
    border: 1px solid #f3f4f6;
    border-radius: 24px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
    overflow: visible;
  }

  &__section {
    padding: 32px 24px 0;

    &--text {
      padding-bottom: 16px;
    }
  }

  &__section-title {
    margin-bottom: 8px;
    color: #171717;
    font-size: 32px;
    font-weight: 600;
    line-height: 44px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 32px;
    padding: 28px 0;
    border-bottom: 1px solid #ebebeb;
  }

  &__label {
    flex-shrink: 0;
    width: 240px;
    color: #999;
    font-size: 28px;
    line-height: 40px;
  }

  &__value {
    flex: 1;
    color: #171717;
    font-size: 28px;
    line-height: 40px;
    text-align: right;
    word-break: break-word;
  }

  &__paragraph {
    margin-top: 16px;
    color: #666;
    font-size: 26px;
    line-height: 40px;
  }

  &__footer {
    flex-shrink: 0;
    z-index: 10;
    display: flex;
    gap: 24px;
    padding: 20px 24px calc(20px + env(safe-area-inset-bottom));
    background: #fff;
    box-shadow: 0 -4px 20px rgba(15, 23, 42, 0.06);
  }

  &__btn {
    flex: 1;
    height: 88px;
    border: none;
    border-radius: 44px;
    font-size: 30px;
    font-weight: 500;
    line-height: 88px;

    &.is-primary {
      background: #1677ff;
      color: #fff;
    }

    &.is-dark {
      background: #171832;
      color: #fff;
    }
  }

  &.black-theme {
    background: #0a0b0f;

    .private-fund-details__summary {
      background: #1b1c24;
      box-shadow: none;
    }

    .private-fund-details__name,
    .private-fund-details__metric-value,
    .private-fund-details__section-title,
    .private-fund-details__value {
      color: #fff;
    }

    .private-fund-details__metric-value.is-rise {
      color: #e83324;
    }

    .private-fund-details__metric-value.is-fall {
      color: #02aa39;
    }

    .private-fund-details__metric-value.is-flat {
      color: #9d9d9f;
    }

    .private-fund-details__metric-label,
    .private-fund-details__label,
    .private-fund-details__paragraph {
      color: #9d9d9f;
    }

    .private-fund-details__tabs {
      border-bottom-color: #323442;
    }

    .private-fund-details__tab {
      color: #9d9d9f;

      &.is-active {
        color: #1353c8;

        &::after {
          background: #1353c8;
        }
      }
    }

    .private-fund-details__performance-card {
      background: #1b1c24;
      border-color: #323442;
      box-shadow: none;
    }

    .private-fund-details__row {
      border-bottom-color: #323442;
    }

    .private-fund-details__footer {
      background: #1b1c24;
      box-shadow: none;
    }

    .private-fund-details__btn.is-primary {
      background: #377cfb;
    }

    .private-fund-details__btn.is-dark {
      background: #377cfb;
    }
  }
}
</style>
